/**
 * Approvals & Modifications Page (KAN-134, KAN-169)
 *
 * Three-tab layout:
 *   1. Pending Requests — trades with pending modification requests + quote approval requests
 *   2. Eligible Trades — Confirmed / ContractSigned trades available for modification
 *   3. History — completed and rejected modifications + quote approvals
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { RefreshCw, AlertCircle, Inbox, Loader2, CheckCircle2, XCircle, Mail, Trash2, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, Clock, FileText, X } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../../components/auth/AuthContext';
import Toast from '../../components/common/Toast';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { modificationService } from '../../services/api/modificationService';
import { clientService } from '../../services/clientService';
import { quoteApprovalService } from '../../services/api/quoteApprovalService';
import InitiateModificationDialog from '../../components/modifications/InitiateModificationDialog';
import ModificationDetailPanel from '../../components/modifications/ModificationDetailPanel';
import QuoteApprovalDetailPanel from '../../components/modifications/QuoteApprovalDetailPanel';
import ModificationHistoryPanel from '../../components/modifications/ModificationHistoryPanel';
import ChildGroupingConfirmModal from '../../components/modifications/ChildGroupingConfirmModal';
import type { ModificationTrade, ModificationHistoryEntry } from '../../types/modification';
import InlineMenu, { InlineMenuItem } from '../../components/common/InlineMenu';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { parseAmountInput } from '../../utils/amountFormatter';
import './Modifications.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type TabId = 'pending' | 'eligible' | 'history' | 'received';

interface DivertedEmail {
  messageId: string;
  clientId: string;
  bankName: string;
  subject: string;
  sender: string;
  emailDate?: string;
  emailBodyPreview?: string;
  pdfStoragePath?: string;
  routerIntent: string;
  routerConfidence: number;
  routerRationale?: string;
  status: string;
  receivedAt?: string;

  // KAN-266: Multi-trade support
  emailTradeIndex?: number;
  tradeIdentifyingInfo?: {
    bankTradeNumber?: string;
    currencyPair?: string;
    maturityDate?: string;
    direction?: string;
    amount?: string;
  };
  pdfAttachmentIndex?: number;
  tradeCount?: number;
}

const Modifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);
  const clientId = user?.organization?.id || user?.id;

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('pending');

  // Data state
  const [eligibleTrades, setEligibleTrades] = useState<ModificationTrade[]>([]);
  const [pendingTrades, setPendingTrades] = useState<ModificationTrade[]>([]);
  const [historyEntries, setHistoryEntries] = useState<ModificationHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedEligibleTrade, setSelectedEligibleTrade] = useState<ModificationTrade | null>(null);
  const [selectedPendingTrade, setSelectedPendingTrade] = useState<ModificationTrade | null>(null);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState<ModificationHistoryEntry | null>(null);

  // Dialog state
  const [showInitiateDialog, setShowInitiateDialog] = useState(false);
  const [showCancelModDialog, setShowCancelModDialog] = useState(false);
  const [showManualConfirmDialog, setShowManualConfirmDialog] = useState(false);

  // Eligible trades date range filter (by Trade Date)
  const [eligibleDateFrom, setEligibleDateFrom] = useState('');
  const [eligibleDateTo, setEligibleDateTo] = useState('');

  // History toggle
  const [showInternalTrades, setShowInternalTrades] = useState(false);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Diverted modification emails (KAN-225d)
  const [divertedEmails, setDivertedEmails] = useState<DivertedEmail[]>([]);
  const [divertedLoading, setDivertedLoading] = useState(false);

  // Trades awaiting bank confirmation (PendingModification state)
  const [awaitingConfirmationTrades, setAwaitingConfirmationTrades] = useState<any[]>([]);
  const [awaitingExpanded, setAwaitingExpanded] = useState(true);

  // Confirmation result modal state
  const [confirmationResult, setConfirmationResult] = useState<any>(null);

  // Child groupings modal — shown after GroupingChange edit-in-place confirm
  const [pendingChildGroupings, setPendingChildGroupings] = useState<{
    parentTradeId: string;
    parentEntityId?: string;
    updates: any[];
  } | null>(null);

  // Grid refs
  const eligibleGridRef = useRef<AgGridReact>(null);
  const pendingGridRef = useRef<AgGridReact>(null);
  const historyGridRef = useRef<AgGridReact>(null);
  const eligibleGridContainerRef = useRef<HTMLDivElement>(null);

  // Context menu state (right-click on eligible trades grid)
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuTrade, setContextMenuTrade] = useState<ModificationTrade | null>(null);

  // Role helpers
  const canInitiate = user?.role === 'client_admin' || user?.role === 'client_bo_user' || user?.role === 'client_fo_bo_user';
  const canConfirmReject = user?.role === 'client_admin' || user?.role === 'client_fo_user' || user?.role === 'client_fo_bo_user';

  /** True when a trade cannot have a new modification initiated (already pending or awaiting bank). */
  const isTradeBlocked = (trade: any) =>
    trade?.pendingModification?.status === 'pending' || trade?.workflowState === 'PendingModification';

  // ----------------------------------------------------------------
  // Data loading
  // ----------------------------------------------------------------

  const loadEligibleTrades = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await modificationService.getEligibleTrades(clientId);
      if (response.success) {
        setEligibleTrades(response.data || []);
      }
    } catch (err) {
      console.error('Error loading eligible trades:', err);
    }
  }, [clientId]);

  const loadPendingTrades = useCallback(async () => {
    if (!clientId) return;
    try {
      const [modResponse, qaResponse] = await Promise.all([
        modificationService.getPendingModifications(clientId),
        quoteApprovalService.getPendingApprovals(clientId),
      ]);
      const modTrades = (modResponse.success ? modResponse.data || [] : [])
        .map((t: any) => ({ ...t, _requestType: 'modification' as const }));
      const qaTrades = (qaResponse.success ? qaResponse.data || [] : [])
        .map((t: any) => ({ ...t, _requestType: 'quote_approval' as const }));
      setPendingTrades([...modTrades, ...qaTrades]);
    } catch (err) {
      console.error('Error loading pending requests:', err);
    }
  }, [clientId]);

  const loadHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const [modResponse, qaResponse] = await Promise.all([
        modificationService.getModificationHistory(clientId),
        quoteApprovalService.getApprovalHistory(clientId),
      ]);
      const modEntries = (modResponse.success ? modResponse.data || [] : [])
        .map((e: any) => ({ ...e, _requestType: 'modification' as const }));
      // Expand each trade into multiple history rows:
      // 1) Past actions from quoteApproval.history array (archived rejections)
      // 2) Current state (the latest approve/reject)
      const qaEntries: any[] = [];
      for (const t of (qaResponse.success ? qaResponse.data || [] : [])) {
        const qa = t.quoteApproval || {};
        // Past actions (archived rejections from re-requests)
        const historyArr = qa.history || [];
        for (const entry of historyArr) {
          qaEntries.push({
            ...t,
            _requestType: 'quote_approval' as const,
            _historyId: `${t.id}_${entry.at}`,
            originalTradeNumber: t.TradeNumber,
            modificationType: 'DraftTradeApproval',
            modificationStatus: entry.action === 'approved' ? 'confirmed' : 'rejected',
            resolvedByName: entry.byName,
            resolvedAt: entry.at,
            rejectionReason: entry.reason,
            initiatedByName: entry.requestedByName,
          });
        }
        // Current state (the latest action)
        qaEntries.push({
          ...t,
          _requestType: 'quote_approval' as const,
          _historyId: `${t.id}_current`,
          originalTradeNumber: t.TradeNumber,
          modificationType: 'DraftTradeApproval',
          modificationStatus: qa.status === 'approved' ? 'confirmed' : 'rejected',
          resolvedByName: qa.status === 'approved'
            ? qa.approvedByName
            : qa.rejectedByName,
          resolvedAt: qa.status === 'approved'
            ? qa.approvedAt
            : qa.rejectedAt,
          rejectionReason: qa.rejectionReason,
          initiatedByName: qa.requestedByName,
        });
      }
      setHistoryEntries([...modEntries, ...qaEntries]);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  }, [clientId]);

  const loadDivertedEmails = useCallback(async () => {
    if (!clientId) return;
    setDivertedLoading(true);
    try {
      const result = await clientService.getDivertedEmails(clientId);
      if (result.success) {
        setDivertedEmails(result.data || []);
      }
    } catch {
      // silent
    } finally {
      setDivertedLoading(false);
    }
  }, [clientId]);

  const loadAwaitingConfirmation = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await clientService.getPendingModificationTrades(clientId);
      if (result.success) {
        setAwaitingConfirmationTrades(result.data || []);
      }
    } catch {
      // silent
    }
  }, [clientId]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadEligibleTrades(), loadPendingTrades(), loadHistory(), loadDivertedEmails(), loadAwaitingConfirmation()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadEligibleTrades, loadPendingTrades, loadHistory, loadDivertedEmails, loadAwaitingConfirmation]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Right-click context menu for eligible trades grid
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellElement = target.closest('.ag-cell');
      if (!cellElement) return;
      if (!eligibleGridContainerRef.current?.contains(target)) return;

      e.preventDefault();
      e.stopPropagation();

      const rowElement = cellElement.closest('[role="row"]');
      if (rowElement && eligibleGridRef.current?.api) {
        const rowIndex = rowElement.getAttribute('row-index');
        if (rowIndex !== null) {
          const rowNode = eligibleGridRef.current.api.getDisplayedRowAtIndex(parseInt(rowIndex));
          if (rowNode) {
            rowNode.setSelected(true, true);
            setSelectedEligibleTrade(rowNode.data);
            setContextMenuTrade(rowNode.data);
            setContextMenuPosition({ x: e.clientX, y: e.clientY });
            setShowContextMenu(true);
          }
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // ----------------------------------------------------------------
  // Event handlers
  // ----------------------------------------------------------------

  const handleRefresh = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  const handleDismissDiverted = useCallback(async (emailItem: DivertedEmail): Promise<boolean> => {
    if (!clientId) return false;
    const result = await clientService.dismissDivertedEmail(clientId, emailItem.messageId, emailItem.emailTradeIndex);
    if (result.success) {
      // KAN-266: Remove only the specific trade row (composite key), not all rows for the email
      const dismissedKey = `${emailItem.messageId}-${emailItem.emailTradeIndex ?? 0}`;
      setDivertedEmails(prev => prev.filter(e => `${e.messageId}-${e.emailTradeIndex ?? 0}` !== dismissedKey));
    }
    return result.success;
  }, [clientId]);

  const handleInitiateClick = useCallback(() => {
    if (!selectedEligibleTrade) return;
    setShowInitiateDialog(true);
  }, [selectedEligibleTrade]);

  const handleInitiateSuccess = useCallback((result: any) => {
    setShowInitiateDialog(false);
    setSelectedEligibleTrade(null);
    const msgKey = result.modificationType === 'Unwind'
      ? 'modifications.messages.initiateSuccessUnwind'
      : 'modifications.messages.initiateSuccess';
    setToastMessage(t(msgKey, { tradeNumber: result.tradeNumber }));
    setToastType('success');
    setShowToast(true);
    loadAllData();
  }, [loadAllData, t]);

  const handleConfirmSuccess = useCallback((result: any) => {
    // Capture child groupings before closing the panel (selectedPendingTrade will be cleared)
    if (result.editedInPlace && result.pendingChildUpdates?.length > 0) {
      setPendingChildGroupings({
        parentTradeId: result.tradeId,
        parentEntityId: selectedPendingTrade?.OurPartyId,
        updates: result.pendingChildUpdates,
      });
    }
    setConfirmationResult({
      ...result,
      originalTradeNumber: selectedPendingTrade?.TradeNumber,
      action: 'confirmed',
    });
    setSelectedPendingTrade(null);
    loadAllData();
  }, [loadAllData, selectedPendingTrade]);

  const handleRejectSuccess = useCallback((reason: string) => {
    setConfirmationResult({
      originalTradeNumber: selectedPendingTrade?.TradeNumber,
      action: 'rejected',
      rejectionReason: reason,
    });
    setSelectedPendingTrade(null);
    loadAllData();
  }, [loadAllData, selectedPendingTrade]);

  const handleError = useCallback((message: string) => {
    setToastMessage(message);
    setToastType('error');
    setShowToast(true);
  }, []);

  // Cancel a PendingModification trade (revert to pre-modification state)
  const handleCancelPendingModification = useCallback(async () => {
    if (!clientId || !contextMenuTrade) return;
    try {
      await modificationService.cancelPendingModification(clientId, contextMenuTrade.id, contextMenuTrade.OurPartyId);
      setSelectedEligibleTrade(null);
      setToastMessage(t('modifications.cancelModDialog.success'));
      setToastType('success');
      setShowToast(true);
      loadAllData();
    } catch (err: any) {
      setToastMessage(err?.message || t('modifications.cancelModDialog.error'));
      setToastType('error');
      setShowToast(true);
    }
    setShowCancelModDialog(false);
  }, [clientId, contextMenuTrade, t, loadAllData]);

  // Manually confirm a PendingModification trade without counterparty email
  const handleConfirmWithoutCounterparty = useCallback(async () => {
    if (!clientId || !contextMenuTrade) return;
    try {
      const response = await modificationService.confirmWithoutCounterparty(clientId, contextMenuTrade.id, contextMenuTrade.OurPartyId);
      setSelectedEligibleTrade(null);
      const isUnwind = response.data?.modificationType === 'Unwind';
      setToastMessage(t(isUnwind ? 'modifications.manualConfirmDialog.successUnwind' : 'modifications.manualConfirmDialog.success'));
      setToastType('success');
      setShowToast(true);
      loadAllData();
    } catch (err: any) {
      setToastMessage(err?.message || t('modifications.manualConfirmDialog.error'));
      setToastType('error');
      setShowToast(true);
    }
    setShowManualConfirmDialog(false);
  }, [clientId, contextMenuTrade, t, loadAllData]);

  // ----------------------------------------------------------------
  // Formatting helpers
  // ----------------------------------------------------------------

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (value: any): string => {
    if (!value) return '';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };

  // ----------------------------------------------------------------
  // Filtered history data
  // ----------------------------------------------------------------

  const filteredHistory = useMemo(() => {
    if (showInternalTrades) return historyEntries;
    return historyEntries.filter((entry: any) => !entry.isInternalTrade);
  }, [historyEntries, showInternalTrades]);

  // Parse a date string in either YYYY-MM-DD or DD-MM-YYYY to a Date
  const parseTradeDate = (value: any): Date | null => {
    if (!value) return null;
    const s = String(value).substring(0, 10);
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s);
    // DD-MM-YYYY
    const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}`);
    return null;
  };

  const filteredEligibleTrades = useMemo(() => {
    if (!eligibleDateFrom && !eligibleDateTo) return eligibleTrades;
    const from = eligibleDateFrom ? new Date(eligibleDateFrom) : null;
    const to = eligibleDateTo ? new Date(eligibleDateTo) : null;
    return eligibleTrades.filter((trade) => {
      const maturity = parseTradeDate(trade.TradeDate);
      if (!maturity) return true;
      if (from && maturity < from) return false;
      if (to && maturity > to) return false;
      return true;
    });
  }, [eligibleTrades, eligibleDateFrom, eligibleDateTo]);

  // ----------------------------------------------------------------
  // Column definitions
  // ----------------------------------------------------------------

  const eligibleColumns: ColDef[] = useMemo(() => [
    { headerName: t('modifications.columns.tradeNumber'), field: 'TradeNumber', width: 110, sortable: true, filter: true },
    { headerName: t('modifications.columns.bankTradeNumber'), field: 'bankTradeNumber', width: 120, filter: true },
    { headerName: t('modifications.columns.legacyReference1'), field: 'LegacyReference1', width: 120, filter: true },
    { headerName: t('modifications.columns.legacyReference2'), field: 'LegacyReference2', width: 120, filter: true },
    { headerName: t('modifications.columns.tradeDate'), field: 'TradeDate', width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
    { headerName: t('modifications.columns.maturityDate'), field: 'MaturityDate', width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
    { headerName: t('modifications.columns.direction'), field: 'Direction', width: 80 },
    { headerName: t('modifications.columns.productType'), field: 'ProductType', width: 130,
      valueGetter: (params: any) => {
        const pt = params.data?.ProductType || '';
        if (pt === 'Forward') {
          if (params.data?.IsForwardStarting) return 'Forward (Starting)';
          if (params.data?.IsAverageRateForward) return 'Forward (Asiático)';
        }
        return pt;
      },
    },
    { headerName: t('modifications.columns.amount'), field: 'QuantityCurrency1', width: 120, sortable: true, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.ccy1'), field: 'Currency1', width: 70 },
    { headerName: t('modifications.columns.ccy2'), field: 'Currency2', width: 70 },
    { headerName: t('modifications.columns.price'), field: 'Price', width: 100, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.counterparty'), field: 'CounterpartyName', width: 140, filter: true },
    { headerName: t('modifications.columns.entity'), field: 'OurParty', width: 140, filter: true },
    { headerName: t('modifications.columns.settlementType'), field: 'SettlementType', width: 130, filter: true },
    { headerName: t('modifications.columns.state'), field: 'workflowState', width: 120, filter: true },
    {
      headerName: t('modifications.columns.allocations'),
      field: 'hasAllocations',
      width: 100,
      cellRenderer: (params: any) => {
        if (params.value) {
          return React.createElement('span', { style: { color: '#60a5fa' } },
            `${t('common.yes')} (${params.data?.allocationCount || '?'})`
          );
        }
        return '';
      }
    },
  ], [t]);

  const pendingColumns: ColDef[] = useMemo(() => [
    { headerName: t('modifications.columns.tradeNumber'), field: 'TradeNumber', width: 110, sortable: true, filter: true },
    {
      headerName: t('modifications.columns.type', 'Type'),
      field: '_requestType',
      width: 140,
      filter: true,
      valueGetter: (p: any) => {
        if (p.data?._requestType === 'quote_approval') return t('modifications.types.draftTradeApproval', 'Trade Approval');
        return p.data?.pendingModification?.modificationType || '';
      },
    },
    { headerName: t('modifications.columns.tradeDate'), field: 'TradeDate', width: 110, valueFormatter: (p: any) => formatDate(p.value) },
    { headerName: t('modifications.columns.maturityDate'), field: 'MaturityDate', width: 110, valueFormatter: (p: any) => formatDate(p.value) },
    { headerName: t('modifications.columns.direction'), field: 'Direction', width: 80 },
    { headerName: t('modifications.columns.amount'), field: 'QuantityCurrency1', width: 120, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.ccy1'), field: 'Currency1', width: 70 },
    { headerName: t('modifications.columns.price'), field: 'Price', width: 100, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.spotPrice'), field: 'SpotPrice', width: 100, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.fwdPts'), field: 'ForwardPoints', width: 100, valueFormatter: (p: any) => formatNumber(p.value) },
    { headerName: t('modifications.columns.settlementType'), field: 'SettlementType', width: 130, filter: true },
    { headerName: t('modifications.columns.counterparty'), field: 'CounterpartyName', width: 140, filter: true },
    { headerName: t('modifications.columns.entity'), field: 'OurParty', width: 140, filter: true },
    {
      headerName: t('modifications.columns.requestedBy'),
      width: 130,
      valueGetter: (p: any) => {
        if (p.data?._requestType === 'quote_approval') return p.data?.quoteApproval?.requestedByName || '';
        return p.data?.pendingModification?.initiatedByName || '';
      },
    },
    {
      headerName: t('modifications.columns.requestDate'),
      width: 130,
      valueGetter: (p: any) => {
        const at = p.data?._requestType === 'quote_approval'
          ? p.data?.quoteApproval?.requestedAt
          : p.data?.pendingModification?.initiatedAt;
        return at ? formatDate(at) : '';
      },
    },
  ], [t]);

  const historyColumns: ColDef[] = useMemo(() => [
    { headerName: t('modifications.columns.originalTrade'), field: 'originalTradeNumber', width: 120, sortable: true, filter: true },
    { headerName: t('modifications.columns.newTrade'), field: 'newTradeNumber', width: 120 },
    { headerName: t('modifications.columns.bankTradeNumber'), field: 'bankTradeNumber', width: 120, filter: true },
    { headerName: t('modifications.columns.legacyReference1'), field: 'LegacyReference1', width: 120, filter: true },
    { headerName: t('modifications.columns.legacyReference2'), field: 'LegacyReference2', width: 120, filter: true },
    { headerName: t('modifications.columns.modType'), field: 'modificationType', width: 140 },
    {
      headerName: t('modifications.columns.modificationStatus'),
      field: 'modificationStatus',
      width: 150,
      filter: true,
      filterValueGetter: (params) => {
        const status = params.data?.modificationStatus;
        if (status === 'confirmed') return t('modifications.statusLabels.confirmed');
        if (status === 'confirmed_inplace') return t('modifications.statusLabels.confirmedInPlace');
        if (status === 'rejected') return t('modifications.statusLabels.rejected');
        return status || '';
      },
      cellRenderer: (params: any) => {
        const status = params.value;
        const className = status === 'confirmed' || status === 'confirmed_inplace'
          ? 'modifications-status-confirmed'
          : status === 'rejected'
            ? 'modifications-status-rejected'
            : '';
        const label = status === 'confirmed'
          ? t('modifications.statusLabels.confirmed')
          : status === 'confirmed_inplace'
            ? t('modifications.statusLabels.confirmedInPlace')
            : status === 'rejected'
              ? t('modifications.statusLabels.rejected')
              : status || '';
        return React.createElement('span', { className }, label);
      },
    },
    {
      headerName: t('modifications.columns.changedFields'),
      field: 'changedFields',
      width: 180,
      valueFormatter: (p: any) => {
        const fields = p.value;
        if (!fields || !Array.isArray(fields) || fields.length === 0) return '';
        const HIDDEN_UNWIND = ['UnwindCalculationMode', 'UnwindSettlementOverridden', 'UnwindSettlementCurrency', 'UnwindGrossResult', 'UnwindDiscountConvention', 'UnwindCargarAccountId', 'UnwindAbonarAccountId'];
        return fields.filter((f: string) => !HIDDEN_UNWIND.includes(f)).map((f: string) => {
          const camel = f.charAt(0).toLowerCase() + f.slice(1);
          return t(`modifications.fields.${camel}`, f);
        }).join(', ');
      },
    },
    { headerName: t('modifications.columns.counterparty'), field: 'CounterpartyName', width: 140, filter: true },
    { headerName: t('modifications.columns.entity'), field: 'OurParty', width: 140, filter: true },
    { headerName: t('modifications.columns.maturityDate'), field: 'MaturityDate', width: 110, valueFormatter: (p: any) => formatDate(p.value) },
    { headerName: t('modifications.columns.initiatedBy'), field: 'initiatedByName', width: 130 },
    { headerName: t('modifications.columns.resolvedBy'), field: 'resolvedByName', width: 130 },
    {
      headerName: t('modifications.columns.resolvedAt'),
      field: 'resolvedAt',
      width: 130,
      valueFormatter: (p: any) => formatDate(p.value),
    },
    {
      headerName: t('modifications.columns.rejectionReason'),
      field: 'rejectionReason',
      width: 180,
      filter: true,
    },
  ], [t]);

  // ----------------------------------------------------------------
  // Grid default column def
  // ----------------------------------------------------------------

  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    minWidth: 60,
  }), []);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  const renderGrid = () => {
    if (loading) {
      return (
        <div className="modifications-loading">
          <Loader2 size={32} className="modifications-spinner" />
          {t('common.loading')}
        </div>
      );
    }

    if (error) {
      return (
        <div className="modifications-error">
          <AlertCircle size={18} />
          {error}
        </div>
      );
    }

    switch (activeTab) {
      case 'eligible':
        return eligibleTrades.length === 0 ? (
          <div className="modifications-empty">
            <div className="modifications-empty-content">
              <Inbox size={36} className="modifications-empty-icon" />
              <span>{t('modifications.empty.eligible')}</span>
            </div>
          </div>
        ) : filteredEligibleTrades.length === 0 ? (
          <div className="modifications-empty">
            <div className="modifications-empty-content">
              <Inbox size={36} className="modifications-empty-icon" />
              <span>{t('modifications.empty.eligibleFiltered')}</span>
            </div>
          </div>
        ) : (
          <div ref={eligibleGridContainerRef} className="modifications-grid-container ag-theme-alpine-dark">
            <AgGridReact
              localeText={agGridLocale}
              ref={eligibleGridRef}
              rowData={filteredEligibleTrades}
              columnDefs={eligibleColumns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.id}
              rowSelection="single"
              onRowClicked={(event) => setSelectedEligibleTrade(event.data)}
              suppressContextMenu={true}
              pagination={true}
              paginationPageSize={50}
              getRowClass={(params) =>
                isTradeBlocked(params.data)
                  ? 'modifications-row-pending-mod'
                  : undefined
              }
            />
          </div>
        );

      case 'pending':
        return pendingTrades.length === 0 && awaitingConfirmationTrades.length === 0 ? (
          <div className="modifications-empty">
            <div className="modifications-empty-content">
              <Inbox size={36} className="modifications-empty-icon" />
              <span>{t('modifications.empty.pending')}</span>
            </div>
          </div>
        ) : (
          <div className="modifications-pending-layout">
            <div className="modifications-pending-main">
              {pendingTrades.length > 0 && (
                <div className="modifications-grid-container ag-theme-alpine-dark">
                  <AgGridReact
                    localeText={agGridLocale}
                    ref={pendingGridRef}
                    rowData={pendingTrades}
                    columnDefs={pendingColumns}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.id}
                    rowSelection="single"
                    onRowClicked={(event) => setSelectedPendingTrade(event.data)}
                    suppressContextMenu={true}
                    pagination={true}
                    paginationPageSize={50}
                  />
                </div>
              )}

              {/* Awaiting Bank Confirmation sub-section */}
              {awaitingConfirmationTrades.length > 0 && (
                <div className="awaiting-confirmation-section">
                  <button
                    className="awaiting-confirmation-header"
                    onClick={() => setAwaitingExpanded(prev => !prev)}
                  >
                    <ChevronDown
                      size={16}
                      className={`awaiting-chevron ${awaitingExpanded ? '' : 'collapsed'}`}
                    />
                    <Clock size={16} />
                    <span className="awaiting-confirmation-title">
                      {t('modifications.awaitingConfirmation.title')}
                    </span>
                    <span className="awaiting-confirmation-count">
                      {awaitingConfirmationTrades.length}
                    </span>
                  </button>
                  {awaitingExpanded && (
                    <div className="awaiting-confirmation-list">
                      <table className="awaiting-confirmation-table">
                        <thead>
                          <tr>
                            <th>{t('modifications.columns.tradeNumber')}</th>
                            <th>{t('modifications.columns.counterparty')}</th>
                            <th>{t('modifications.columns.modType')}</th>
                            <th>{t('modifications.columns.direction')}</th>
                            <th>{t('modifications.columns.currencyPair')}</th>
                            <th>{t('modifications.columns.amount')}</th>
                            <th>{t('modifications.columns.maturityDate')}</th>
                            <th>{t('modifications.columns.approvedBy')}</th>
                            <th>{t('modifications.columns.approvedAt')}</th>
                            <th>{t('modifications.columns.entity')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {awaitingConfirmationTrades.map(trade => {
                            const modTypeKey = trade.modificationType
                              ? `modifications.types.${trade.modificationType.charAt(0).toLowerCase()}${trade.modificationType.slice(1)}`
                              : '';
                            return (
                            <tr key={trade.id}>
                              <td className="awaiting-trade-number">{trade.TradeNumber}</td>
                              <td>{trade.CounterpartyName}</td>
                              <td>
                                <span className="awaiting-mod-type-badge">
                                  {modTypeKey ? t(modTypeKey, trade.modificationType) : '—'}
                                </span>
                              </td>
                              <td>{trade.Direction || '—'}</td>
                              <td>{trade.Currency1}/{trade.Currency2}</td>
                              <td className="awaiting-amount">
                                {trade.QuantityCurrency1 != null
                                  ? Number(trade.QuantityCurrency1).toLocaleString(i18n.language, { maximumFractionDigits: 0 })
                                  : '—'}
                              </td>
                              <td>
                                {trade.MaturityDate
                                  ? new Date(trade.MaturityDate).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
                                  : '—'}
                              </td>
                              <td>{trade.confirmedByName || '—'}</td>
                              <td>
                                {trade.confirmedAt
                                  ? new Date(trade.confirmedAt).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
                                  : '—'}
                              </td>
                              <td>{trade.OurParty || '—'}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedPendingTrade && clientId && (
              (selectedPendingTrade as any)._requestType === 'quote_approval' ? (
                <QuoteApprovalDetailPanel
                  trade={selectedPendingTrade}
                  clientId={clientId}
                  onApproveSuccess={() => {
                    setToastMessage(t('modifications.quoteApproval.approved', 'Approval granted'));
                    setToastType('success');
                    setShowToast(true);
                    setSelectedPendingTrade(null);
                    loadAllData();
                  }}
                  onRejectSuccess={() => {
                    setToastMessage(t('modifications.quoteApproval.rejected', 'Approval rejected'));
                    setToastType('success');
                    setShowToast(true);
                    setSelectedPendingTrade(null);
                    loadAllData();
                  }}
                  onError={handleError}
                  onClose={() => setSelectedPendingTrade(null)}
                />
              ) : (
                <ModificationDetailPanel
                  trade={selectedPendingTrade}
                  clientId={clientId}
                  canConfirmReject={canConfirmReject}
                  onConfirmSuccess={handleConfirmSuccess}
                  onRejectSuccess={handleRejectSuccess}
                  onError={handleError}
                  onClose={() => setSelectedPendingTrade(null)}
                />
              )
            )}
          </div>
        );

      case 'received':
        return (
          <ReceivedModificationsTab
            items={divertedEmails}
            loading={divertedLoading}
            clientId={clientId}
            onDismiss={handleDismissDiverted}
            onMatchSuccess={loadAllData}
          />
        );

      case 'history':
        return historyEntries.length === 0 ? (
          <div className="modifications-empty">
            <div className="modifications-empty-content">
              <Inbox size={36} className="modifications-empty-icon" />
              <span>{t('modifications.empty.history')}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="modifications-action-bar">
              <div className="modifications-action-bar-left">
                {t('modifications.historyCount', { count: filteredHistory.length })}
              </div>
              <div className="modifications-action-bar-right">
                <label className="modifications-toggle-label">
                  <input
                    type="checkbox"
                    checked={showInternalTrades}
                    onChange={(e) => setShowInternalTrades(e.target.checked)}
                  />
                  {t('modifications.showInternalTrades')}
                </label>
              </div>
            </div>
            <div className="modifications-pending-layout">
              <div className="modifications-grid-container ag-theme-alpine-dark">
                <AgGridReact
                  localeText={agGridLocale}
                  ref={historyGridRef}
                  rowData={filteredHistory}
                  columnDefs={historyColumns}
                  defaultColDef={defaultColDef}
                  getRowId={(params) => params.data._historyId || params.data.id}
                  rowSelection="single"
                  onRowClicked={(event) => setSelectedHistoryEntry(event.data)}
                  suppressContextMenu={true}
                  pagination={true}
                  paginationPageSize={50}
                />
              </div>
              {selectedHistoryEntry && (
                <ModificationHistoryPanel
                  entry={selectedHistoryEntry}
                  clientId={clientId || ''}
                  onDocumentGenerated={(results) => {
                    const ok = results.filter(r => r.status === 'SUCCESS').length;
                    const fail = results.filter(r => r.status !== 'SUCCESS').length;
                    if (ok > 0) {
                      setToastMessage(t('modifications.generateFormSuccess'));
                      setToastType('success');
                      setShowToast(true);
                    }
                    if (fail > 0) {
                      setToastMessage(t('modifications.generateFormError'));
                      setToastType('error');
                      setShowToast(true);
                    }
                  }}
                  onClose={() => setSelectedHistoryEntry(null)}
                />
              )}
            </div>
          </>
        );
    }
  };

  return (
    <div className="modifications-page">
      {/* Header */}
      <div className="modifications-header">
        <div className="modifications-header-left">
          <h1 className="modifications-title">{t('modifications.title')}</h1>
        </div>
        <div className="modifications-header-right">
          <button className="modifications-refresh-btn" onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={14} />
            {t('modifications.refresh')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="modifications-tabs">
        <button
          className={`modifications-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('modifications.tabs.pending')}
          {pendingTrades.length > 0 && (
            <span className="modifications-tab-badge">{pendingTrades.length}</span>
          )}
        </button>
        <button
          className={`modifications-tab ${activeTab === 'eligible' ? 'active' : ''}`}
          onClick={() => setActiveTab('eligible')}
        >
          {t('modifications.tabs.eligible')}
        </button>
        <button
          className={`modifications-tab ${activeTab === 'received' ? 'active' : ''}`}
          onClick={() => setActiveTab('received')}
        >
          {t('modifications.tabs.received')}
          {divertedEmails.length > 0 && (
            <span className="modifications-tab-badge modifications-tab-badge--warning">
              {divertedEmails.length}
            </span>
          )}
        </button>
        <button
          className={`modifications-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t('modifications.tabs.history')}
        </button>
      </div>

      {/* Content */}
      <div className="modifications-content">
        {/* Action bar for eligible tab */}
        {activeTab === 'eligible' && eligibleTrades.length > 0 && (
          <div className="modifications-action-bar">
            <div className="modifications-action-bar-left">
              {selectedEligibleTrade && isTradeBlocked(selectedEligibleTrade)
                ? t('modifications.selectedTradeBlocked', { number: selectedEligibleTrade.TradeNumber })
                : selectedEligibleTrade
                  ? t('modifications.selectedTrade', { number: selectedEligibleTrade.TradeNumber })
                  : t('modifications.selectTrade')
              }
            </div>
            <div className="modifications-action-bar-right">
              <DateRangeFilter
                dateFrom={eligibleDateFrom}
                dateTo={eligibleDateTo}
                onApply={(from, to) => {
                  setEligibleDateFrom(from);
                  setEligibleDateTo(to);
                  setSelectedEligibleTrade(null);
                }}
                label={t('modifications.tradeDateFilter')}
                disabled={loading}
              />
              {canInitiate && (
                <button
                  className="modifications-initiate-btn"
                  onClick={handleInitiateClick}
                  disabled={!selectedEligibleTrade || isTradeBlocked(selectedEligibleTrade)}
                >
                  {t('modifications.initiateBtn')}
                </button>
              )}
            </div>
          </div>
        )}

        {renderGrid()}
      </div>

      {/* Initiate Modification Dialog */}
      {showInitiateDialog && selectedEligibleTrade && clientId && (
        <InitiateModificationDialog
          trade={selectedEligibleTrade}
          clientId={clientId}
          onSuccess={handleInitiateSuccess}
          onCancel={() => setShowInitiateDialog(false)}
          onError={handleError}
        />
      )}

      {/* Confirmation/Rejection Result Modal */}
      {confirmationResult && (
        <div className="modifications-modal-overlay" onClick={() => { setConfirmationResult(null); setAllocsRegenerated(null); }}>
          <div className="modifications-modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 420 }}>
            <div className="modifications-modal-header">
              <h3 className="modifications-modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {confirmationResult.action === 'confirmed'
                  ? <><CheckCircle2 size={20} style={{ color: 'var(--accent-green, #22c55e)' }} /> {confirmationResult.modificationType === 'Unwind' ? t('modifications.resultModal.titleUnwindConfirmed') : t('modifications.resultModal.titleConfirmed')}</>
                  : <><XCircle size={20} style={{ color: '#ef4444' }} /> {t('modifications.resultModal.titleRejected')}</>
                }
              </h3>
              <button className="modifications-modal-close" onClick={() => { setConfirmationResult(null); setAllocsRegenerated(null); }}>&times;</button>
            </div>
            <div className="modifications-modal-body">
              <div className="modifications-result-summary">
                <div className="modifications-result-row">
                  <span className="modifications-result-label">{t('modifications.resultModal.originalTrade')}</span>
                  <span className="modifications-result-value">#{confirmationResult.originalTradeNumber}</span>
                </div>
                {confirmationResult.action === 'confirmed' && (
                  <>
                    <div className="modifications-result-row">
                      <span className="modifications-result-label">{t('modifications.resultModal.action')}</span>
                      <span className="modifications-result-value">
                        {confirmationResult.modificationType === 'Unwind'
                          ? t('modifications.resultModal.actionUnwound')
                          : confirmationResult.modificationType === 'Cancellation'
                            ? t('modifications.resultModal.actionCancelled')
                            : confirmationResult.movedToBooked
                              ? t('modifications.resultModal.actionMovedToBooked')
                              : confirmationResult.editedInPlace
                                ? t('modifications.resultModal.actionEditedInPlace')
                                : t('modifications.resultModal.actionModified')
                        }
                      </span>
                    </div>
                    {confirmationResult.editedInPlace && confirmationResult.modificationType !== 'Unwind' && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-value" style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.82rem' }}>
                          {t('modifications.resultModal.editedInPlaceNote')}
                        </span>
                      </div>
                    )}
                    {confirmationResult.movedToBooked && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-value" style={{ color: 'var(--accent-amber, #f59e0b)', fontSize: '0.82rem' }}>
                          {t('modifications.resultModal.otherNextStep')}
                        </span>
                      </div>
                    )}
                    {confirmationResult.newTradeNumber && !confirmationResult.movedToBooked && !confirmationResult.editedInPlace && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-label">{t('modifications.resultModal.newTrade')}</span>
                        <span className="modifications-result-value modifications-result-highlight">#{confirmationResult.newTradeNumber}</span>
                      </div>
                    )}
                    {confirmationResult.editedInPlace && pendingChildGroupings && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-value" style={{ color: 'var(--accent-amber, #f59e0b)', fontSize: '0.82rem' }}>
                          {t('modifications.resultModal.pendingChildGroupings', { count: pendingChildGroupings.updates.length })}
                        </span>
                      </div>
                    )}
                    {confirmationResult.modificationType && confirmationResult.modificationType !== 'Cancellation' && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-label">{t('modifications.resultModal.modType')}</span>
                        <span className="modifications-result-value">{confirmationResult.modificationType}</span>
                      </div>
                    )}
                    {confirmationResult.changedFieldsSummary && confirmationResult.changedFieldsSummary.length > 0 && (() => {
                      const isUnwindResult = confirmationResult.modificationType === 'Unwind';
                      const headerLabel = isUnwindResult
                        ? t('modifications.resultModal.unwindDetails')
                        : t('modifications.resultModal.changedFields');
                      return (
                      <div className="modifications-result-changes">
                        <span className="modifications-result-label">{headerLabel}</span>
                        <table className="modifications-result-changes-table">
                          <thead>
                            <tr>
                              <th>{t('modifications.detail.fieldLabel')}</th>
                              {!isUnwindResult && <th>{t('modifications.resultModal.from')}</th>}
                              <th>{isUnwindResult ? t('modifications.resultModal.value') : t('modifications.resultModal.to')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {confirmationResult.changedFieldsSummary.map((cf: any, i: number) => (
                              <tr key={i}>
                                <td>{cf.label}</td>
                                {!isUnwindResult && <td className="modifications-result-from">{cf.from}</td>}
                                <td className={isUnwindResult ? '' : 'modifications-result-to'}>{cf.to}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}
                    {(confirmationResult.childrenCancelled > 0 || (confirmationResult.childrenRegenerated ?? 0) > 0) && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-label">{t('modifications.resultModal.allocations')}</span>
                        <span className="modifications-result-value">
                          {(confirmationResult.childrenRegenerated ?? 0) > 0 ? (
                            <span style={{ color: 'var(--accent-green, #22c55e)' }}>
                              {t('modifications.resultModal.allocationsRegenerated', { count: confirmationResult.childrenRegenerated })}
                            </span>
                          ) : (
                            t('modifications.resultModal.allocationsCancelled', { count: confirmationResult.childrenCancelled })
                          )}
                        </span>
                      </div>
                    )}
                    {(confirmationResult.splitsRegenerated ?? 0) > 0 && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-label">{t('modifications.resultModal.splits')}</span>
                        <span className="modifications-result-value" style={{ color: 'var(--accent-green, #22c55e)' }}>
                          {t('modifications.resultModal.splitsRegenerated', { count: confirmationResult.splitsRegenerated })}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {confirmationResult.action === 'rejected' && (
                  <>
                    <div className="modifications-result-row">
                      <span className="modifications-result-label">{t('modifications.resultModal.action')}</span>
                      <span className="modifications-result-value">{t('modifications.resultModal.actionRejected')}</span>
                    </div>
                    {confirmationResult.rejectionReason && (
                      <div className="modifications-result-row">
                        <span className="modifications-result-label">{t('modifications.resultModal.reason')}</span>
                        <span className="modifications-result-value">{confirmationResult.rejectionReason}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modifications-modal-footer">
              <button
                className="modifications-modal-submit-btn"
                onClick={() => setConfirmationResult(null)}
              >
                {t('common.ok') || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Child Groupings Modal — shown after GroupingChange edit-in-place confirm */}
      {!confirmationResult && pendingChildGroupings && clientId && (
        <ChildGroupingConfirmModal
          clientId={clientId}
          parentTradeId={pendingChildGroupings.parentTradeId}
          entityId={pendingChildGroupings.parentEntityId}
          pendingChildUpdates={pendingChildGroupings.updates}
          onApplySuccess={(count) => {
            setPendingChildGroupings(null);
            setToastMessage(t('modifications.childGroupings.applySuccess', { count }));
            setToastType('success');
            setShowToast(true);
          }}
          onError={(message) => {
            setToastMessage(message);
            setToastType('error');
            setShowToast(true);
          }}
        />
      )}

      {/* Right-click context menu (eligible trades grid) */}
      {showContextMenu && contextMenuTrade && (
        <InlineMenu
          position={contextMenuPosition}
          onClose={() => setShowContextMenu(false)}
          items={[
            {
              label: t('modifications.initiateBtn'),
              action: () => {
                setShowContextMenu(false);
                setShowInitiateDialog(true);
              },
              disabled: !canInitiate || isTradeBlocked(contextMenuTrade),
            },
            ...(contextMenuTrade.workflowState === 'PendingModification' ? [
              {
                label: t('modifications.cancelPendingModification'),
                action: () => {
                  setShowContextMenu(false);
                  setShowCancelModDialog(true);
                },
              },
              {
                label: t('modifications.confirmWithoutCounterparty'),
                action: () => {
                  setShowContextMenu(false);
                  setShowManualConfirmDialog(true);
                },
              },
            ] : []),
          ] as InlineMenuItem[]}
        />
      )}

      {/* Cancel Pending Modification confirmation dialog */}
      <ConfirmDialog
        isOpen={showCancelModDialog}
        title={t('modifications.cancelModDialog.title')}
        message={t('modifications.cancelModDialog.message')}
        onConfirm={handleCancelPendingModification}
        onCancel={() => setShowCancelModDialog(false)}
      />

      {/* Confirm Without Counterparty confirmation dialog */}
      <ConfirmDialog
        isOpen={showManualConfirmDialog}
        title={contextMenuTrade?.pendingModification?.modificationType === 'Unwind'
          ? t('modifications.manualConfirmDialog.titleUnwind')
          : t('modifications.manualConfirmDialog.title')
        }
        message={contextMenuTrade?.pendingModification?.modificationType === 'Unwind'
          ? t('modifications.manualConfirmDialog.messageUnwind')
          : t('modifications.manualConfirmDialog.message')
        }
        onConfirm={handleConfirmWithoutCounterparty}
        onCancel={() => setShowManualConfirmDialog(false)}
      />

      {/* Toast */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type={toastType}
      />
    </div>
  );
};

// ========== Received Modifications Tab ==========

const INTENT_LABEL_KEYS: Record<string, string> = {
  trade_modification: 'modifications.received.intentModification',
  confirmation_unwind: 'modifications.received.intentUnwind',
  dispute_notice: 'modifications.received.intentDispute',
  cancellation: 'modifications.received.intentCancellation',
};

const INTENT_COLOURS: Record<string, string> = {
  trade_modification: '#f59e0b',
  confirmation_unwind: '#ef4444',
  dispute_notice: '#8b5cf6',
  cancellation: '#ef4444',
};

// ── List item (condensed, selectable) ──────────────────────────────────────

const formatDivertedDate = (email: DivertedEmail, locale: string): string => {
  const raw = email.emailDate || email.receivedAt;
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw.substring(0, 16);
    return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return raw.substring(0, 16);
  }
};

const ReceivedEmailListItem: React.FC<{
  item: DivertedEmail;
  isSelected: boolean;
  onSelect: (item: DivertedEmail) => void;
  onDismiss: (emailItem: DivertedEmail) => Promise<boolean>;
}> = ({ item, isSelected, onSelect, onDismiss }) => {
  const { t, i18n } = useTranslation();
  const [dismissing, setDismissing] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);

  const intentLabel = t(INTENT_LABEL_KEYS[item.routerIntent] || item.routerIntent);
  const intentColour = INTENT_COLOURS[item.routerIntent] || '#6b7280';

  const handleDismissClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDismissConfirm(true);
  };

  const handleDismissConfirm = async () => {
    setShowDismissConfirm(false);
    setDismissing(true);
    const ok = await onDismiss(item);
    if (!ok) setDismissing(false);
  };

  return (
    <>
      <div
        className={`received-email-list-item ${isSelected ? 'selected' : ''}`}
        onClick={() => onSelect(item)}
      >
        <div className="received-email-list-item-header">
          <span className="received-email-intent-badge" style={{ backgroundColor: intentColour }}>
            {intentLabel}
          </span>
          <span className="received-email-bank">{item.bankName || item.sender}</span>
          <span className="received-email-date">{formatDivertedDate(item, i18n.language)}</span>
          <button
            className="modifications-dismiss-btn"
            onClick={handleDismissClick}
            disabled={dismissing}
            title={t('confirmations.exceptions.dismiss')}
          >
            {dismissing ? <Loader2 size={13} className="modifications-spinner" /> : <Trash2 size={13} />}
          </button>
        </div>
        <div className="received-email-list-item-subject">{item.subject}</div>
        {/* KAN-266: Per-trade identifying info for multi-trade emails */}
        {(item.tradeCount != null && item.tradeCount > 1) && (
          <div className="received-email-trade-info">
            <span className="received-email-trade-counter">
              Trade {(item.emailTradeIndex ?? 0) + 1} of {item.tradeCount}
            </span>
            {item.tradeIdentifyingInfo?.bankTradeNumber && (
              <span className="received-email-trade-detail">BTN: {item.tradeIdentifyingInfo.bankTradeNumber}</span>
            )}
            {item.tradeIdentifyingInfo?.currencyPair && (
              <span className="received-email-trade-detail">{item.tradeIdentifyingInfo.currencyPair}</span>
            )}
            {item.tradeIdentifyingInfo?.maturityDate && (
              <span className="received-email-trade-detail">{item.tradeIdentifyingInfo.maturityDate}</span>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={showDismissConfirm}
        title={t('modifications.received.dismissConfirmTitle')}
        message={t('modifications.received.dismissConfirmMessage')}
        confirmLabel={t('confirmations.exceptions.dismiss')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDismissConfirm}
        onCancel={() => setShowDismissConfirm(false)}
        type="warning"
      />
    </>
  );
};

// ── Detail panel (right side) ──────────────────────────────────────────────

const ReceivedEmailPanel: React.FC<{
  email: DivertedEmail;
  clientId: string;
  onDismiss: (emailItem: DivertedEmail) => Promise<boolean>;
  onMatchSuccess: () => void;
}> = ({ email, clientId, onDismiss, onMatchSuccess }) => {
  const { t, i18n } = useTranslation();
  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);

  // PDF viewer state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState(500);

  // Match pane state
  const [candidateTrades, setCandidateTrades] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [matchingTradeId, setMatchingTradeId] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [expandedCandidates, setExpandedCandidates] = useState<Set<string>>(new Set());

  // LLM validation flow state (Step 4)
  const [showModeModal, setShowModeModal] = useState(false);
  const [modeWarningPending, setModeWarningPending] = useState<'confirmation' | 'contract' | null>(null);
  const [pendingMatchTrade, setPendingMatchTrade] = useState<any | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any | null>(null);
  const [confirmingMatch, setConfirmingMatch] = useState(false);
  const [matchResult, setMatchResult] = useState<any | null>(null);
  const [processingLabel, setProcessingLabel] = useState('');

  // Price cascade modal state (shown when override includes Price on a forward trade)
  const [showPriceCascade, setShowPriceCascade] = useState(false);
  const [priceCascadeTarget, setPriceCascadeTarget] = useState<number | null>(null);
  const [priceCascadeSpot, setPriceCascadeSpot] = useState('');
  const [priceCascadeFwd, setPriceCascadeFwd] = useState('');
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, any> | null>(null);

  // Formatters for AG Grid (trade picker)
  const formatDate = (value: any): string => {
    if (!value) return '';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };
  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Path 3 — Create Modification from counterparty email
  const [showTradePickerInline, setShowTradePickerInline] = useState(false);
  const [tradePickerTrades, setTradePickerTrades] = useState<any[]>([]);
  const [tradePickerLoading, setTradePickerLoading] = useState(false);
  const [selectedPickerTrade, setSelectedPickerTrade] = useState<any | null>(null);
  const [pickerModType, setPickerModType] = useState('');
  const [isPath3Flow, setIsPath3Flow] = useState(false);
  const tradePickerGridRef = useRef<AgGridReact>(null);

  const intentLabel = t(INTENT_LABEL_KEYS[email.routerIntent] || email.routerIntent);
  const intentColour = INTENT_COLOURS[email.routerIntent] || '#6b7280';

  // --- Comparison helpers ---------------------------------------------------

  // Desired display order for comparison fields (earlier = higher)
  const FIELD_ORDER: string[] = [
    'SpotPrice', 'ForwardPoints', 'Price',
    'MaturityDate', 'PaymentDate',
    'Currency1PaymentDate', 'Currency2PaymentDate',
    'SettlementType', 'SettlementCurrency', 'FixingReference',
    'Grouping1', 'Grouping2', 'Grouping3', 'HedgeObject',
  ];

  /** Sort comparison rows by the defined field order. */
  const sortComparisons = (comparisons: any[]) => {
    return [...comparisons].sort((a, b) => {
      const ia = FIELD_ORDER.indexOf(a.field);
      const ib = FIELD_ORDER.indexOf(b.field);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  };

  /** Date fields whose values should be displayed as DD/MM/YYYY. */
  const DATE_FIELDS = new Set([
    'TradeDate', 'ValueDate', 'MaturityDate', 'PaymentDate', 'Currency1PaymentDate', 'Currency2PaymentDate',
  ]);

  const NUMERIC_FIELDS = new Set([
    'QuantityCurrency1', 'QuantityCurrency2', 'Price', 'SpotPrice', 'ForwardPoints',
  ]);
  const AMOUNT_FIELDS = new Set(['QuantityCurrency1', 'QuantityCurrency2']);

  /** Format comparison values: dates as DD/MM/YYYY, numbers with locale separators. */
  const formatComparisonValue = (field: string, value: any): string => {
    if (value == null) return '—';
    const str = String(value);
    if (DATE_FIELDS.has(field)) {
      const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    }
    if (NUMERIC_FIELDS.has(field)) {
      const num = Number(str);
      if (!isNaN(num)) {
        const isAmount = AMOUNT_FIELDS.has(field);
        return num.toLocaleString(i18n.language, {
          minimumFractionDigits: isAmount ? 0 : 2,
          maximumFractionDigits: isAmount ? 0 : 6,
        });
      }
    }
    return str;
  };

  /** Filter + sort comparisons: exclude not_provided and BankTradeNumber. */
  const prepareComparisons = (comparisons: any[]) => {
    return sortComparisons(
      comparisons.filter((c: any) => c.result !== 'not_provided' && c.field !== 'BankTradeNumber')
    );
  };

  // Fetch PDF signed URL when email changes
  useEffect(() => {
    setPdfUrl(null);
    setPdfError(false);
    setPdfNumPages(0);
    setPdfPageNumber(1);

    if (!email.pdfStoragePath) return;

    let cancelled = false;
    setPdfLoading(true);
    // KAN-266: Pass pdfAttachmentIndex for multi-PDF emails
    clientService.getDivertedEmailPdfUrl(clientId, email.messageId, email.pdfAttachmentIndex).then(url => {
      if (cancelled) return;
      setPdfLoading(false);
      if (url) {
        setPdfUrl(url);
      } else {
        setPdfError(true);
      }
    });
    return () => { cancelled = true; };
  }, [email.messageId, email.pdfStoragePath, email.pdfAttachmentIndex, clientId]);

  // Measure container width for PDF rendering
  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setPdfWidth(Math.floor(entry.contentRect.width) - 16);
      }
    });
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Fetch candidate PendingModification trades when email changes
  useEffect(() => {
    setCandidateTrades([]);
    setCandidatesLoading(true);
    setMatchError(null);
    setMatchingTradeId(null);
    // Reset Path 3 trade picker + validation state so it doesn't persist across email selections
    setShowTradePickerInline(false);
    setTradePickerTrades([]);
    setSelectedPickerTrade(null);
    setPickerModType('');
    setIsPath3Flow(false);
    setShowModeModal(false);
    setValidationResult(null);
    setMatchResult(null);
    setPendingMatchTrade(null);

    let cancelled = false;
    clientService.getPendingModificationTrades(clientId, email.bankName || undefined)
      .then(response => {
        if (cancelled) return;
        setCandidatesLoading(false);
        if (response.success && response.data) {
          setCandidateTrades(response.data);
        }
      });
    return () => { cancelled = true; };
  }, [email.messageId, email.emailTradeIndex, email.bankName, clientId]);

  const handleDismissClick = () => {
    setShowDismissConfirm(true);
  };

  const handleDismissConfirm = async () => {
    setShowDismissConfirm(false);
    setDismissing(true);
    setDismissError(false);
    const ok = await onDismiss(email);
    if (!ok) {
      setDismissError(true);
      setDismissing(false);
    }
  };

  // --- Path 3: Create Modification from counterparty email ---
  const handleOpenTradePicker = async () => {
    setShowTradePickerInline(true);
    setTradePickerLoading(true);
    setSelectedPickerTrade(null);
    setPickerModType('');
    try {
      // Use bankId from the email document (resolved from sender domain)
      const counterpartyId = email.bankId || (candidateTrades.length > 0 ? candidateTrades[0]?.CounterpartyId : undefined);
      const response = await modificationService.getEligibleTrades(clientId, undefined, counterpartyId, email.messageId, email.tradeIdentifyingInfo?.bankTradeNumber);
      if (response.success && response.data) {
        const eligible = (response.data as any[]).filter(
          (t: any) => t.workflowState !== 'PendingModification' && !t.hasPendingModification
        );
        // Sort BTN-matched trades to the top
        eligible.sort((a: any, b: any) => (b.btnMatchedInEmail ? 1 : 0) - (a.btnMatchedInEmail ? 1 : 0));
        setTradePickerTrades(eligible);
        // Auto-select if exactly one BTN match
        const btnMatches = eligible.filter((t: any) => t.btnMatchedInEmail);
        if (btnMatches.length === 1) {
          setSelectedPickerTrade(btnMatches[0]);
        }
      }
    } catch {
      setTradePickerTrades([]);
    } finally {
      setTradePickerLoading(false);
    }
  };

  const handleTradePickerContinue = () => {
    if (!selectedPickerTrade || !pickerModType) return;
    // Set up the trade as the pending match target — reuse mode modal flow
    setIsPath3Flow(true);
    setPendingMatchTrade({
      ...selectedPickerTrade,
      _path3ModificationType: pickerModType,
    });
    setShowTradePickerInline(false);
    setShowModeModal(true);
    setModeWarningPending(null);
    setValidationResult(null);
    setMatchError(null);
  };

  // Step 4: clicking Match opens the processing mode modal
  const handleMatch = (trade: any) => {
    setIsPath3Flow(false);
    setPendingMatchTrade(trade);
    setShowModeModal(true);
    setModeWarningPending(null);
    setValidationResult(null);
    setMatchError(null);
  };

  // Check if the selected mode matches the counterparty's preferred method
  const handleModeClick = (mode: 'confirmation' | 'contract') => {
    const method = pendingMatchTrade?.counterpartyModificationMethod;
    if (method) {
      const isMatch = mode === 'contract'
        ? method === 'contract'
        : method.startsWith('confirmation');
      if (!isMatch) {
        setModeWarningPending(mode);
        return;
      }
    }
    handleModeSelect(mode);
  };

  // After user picks "confirmation" or "contract"
  const handleModeSelect = async (mode: 'confirmation' | 'contract') => {
    if (!pendingMatchTrade) return;
    setShowModeModal(false);
    setValidating(true);
    setMatchingTradeId(pendingMatchTrade.id);
    setMatchError(null);
    setProcessingLabel(t('modifications.received.processingValidation'));

    try {
      // Branch: Path 3 (create modification) vs Path 2 (match existing)
      // KAN-266: Pass emailTradeIndex + sourceBankTradeNumber for multi-trade support
      const response = isPath3Flow
        ? await clientService.createModificationFromEmail(
            clientId, email.messageId, pendingMatchTrade.id,
            pendingMatchTrade.entityId || pendingMatchTrade.OurPartyId,
            pendingMatchTrade._path3ModificationType, mode,
            email.emailTradeIndex, email.tradeIdentifyingInfo?.bankTradeNumber
          )
        : await clientService.validateModificationMatch(
            clientId, email.messageId, pendingMatchTrade.id, pendingMatchTrade.entityId, mode,
            email.emailTradeIndex, email.tradeIdentifyingInfo?.bankTradeNumber
          );

      if (!response.success) {
        setMatchError(response.message || t('common.error'));
        setValidating(false);
        setMatchingTradeId(null);
        return;
      }
      const data = response.data;

      if (data.extractionFailed) {
        setMatchError(data.message || 'Extraction failed');
        setValidating(false);
        setMatchingTradeId(null);
        return;
      }

      // Path 3: Identity mismatch — show warning with Cancel / Continue Anyway
      if (isPath3Flow && data.identityMismatch) {
        setValidationResult(data);
        setValidating(false);
        return;
      }

      // Path 3: Price changed on a forward — need SpotPrice + ForwardPoints decomposition
      if (isPath3Flow && data.needsPriceCascade) {
        setValidating(false);
        setPriceCascadeTarget(data.newPrice);
        setPriceCascadeSpot('');
        setPriceCascadeFwd('');
        // Stash data needed for confirm-create-modification after cascade
        setPendingOverrides({});
        setValidationResult(data);
        setShowPriceCascade(true);
        return;
      }

      // Path 3 auto-applied (identity OK — backend already applied the modification)
      if (isPath3Flow && data.applied) {
        setValidating(false);
        setMatchResult({
          action: 'confirmed',
          tradeNumber: data.tradeNumber || pendingMatchTrade.TradeNumber,
          btn: data.btn,
          comparisons: data.comparisons,
          hasDiscrepancies: false,
          path3: true,
          modifiedFields: data.modifiedFields,
        });
        return;
      }

      // No discrepancies → auto-confirm (Path 2 only — Path 3 handled above)
      if (!data.hasDiscrepancies) {
        setValidating(false);
        setConfirmingMatch(true);
        setProcessingLabel(t('modifications.received.processingConfirm'));
        const confirmResp = await clientService.confirmModificationMatch(
          clientId, email.messageId, pendingMatchTrade.id, pendingMatchTrade.entityId,
          data.btn, undefined, email.emailTradeIndex
        );
        setConfirmingMatch(false);
        if (confirmResp.success) {
          setMatchResult({
            action: 'confirmed',
            tradeNumber: pendingMatchTrade.TradeNumber,
            btn: data.btn,
            comparisons: data.comparisons,
            hasDiscrepancies: false,
          });
        } else {
          setMatchError(confirmResp.message || t('common.error'));
          setMatchingTradeId(null);
        }
        return;
      }

      // Discrepancies found → show validation result for user decision
      setValidationResult(data);
      setValidating(false);

    } catch (err) {
      setMatchError(t('common.error'));
      setValidating(false);
      setMatchingTradeId(null);
    }
  };

  // Override & Accept — accept bank values for mismatched fields
  const handleOverrideAccept = async () => {
    if (!pendingMatchTrade || !validationResult) return;

    // Build overrideFields from mismatch comparisons (use counterparty's value)
    const overrides: Record<string, any> = {};
    for (const comp of validationResult.comparisons || []) {
      if (comp.result === 'mismatch' && comp.emailValue != null) {
        overrides[comp.field] = comp.emailValue;
      }
    }

    // If Price is being overridden and the trade has SpotPrice (forward trade),
    // show the price cascade modal so the user can enter the decomposition
    if ('Price' in overrides && pendingMatchTrade.SpotPrice != null) {
      const targetPrice = typeof overrides.Price === 'number'
        ? overrides.Price
        : parseFloat(String(overrides.Price));
      if (!isNaN(targetPrice)) {
        setPendingOverrides(overrides);
        setPriceCascadeTarget(targetPrice);
        setPriceCascadeSpot(String(pendingMatchTrade.SpotPrice ?? ''));
        setPriceCascadeFwd(String(pendingMatchTrade.ForwardPoints ?? '0'));
        setShowPriceCascade(true);
        return; // Wait for modal confirmation
      }
    }

    // No Price cascade needed — proceed directly
    await executeOverrideAccept(overrides);
  };

  /** Actually execute the override accept (called directly or after price cascade). */
  const executeOverrideAccept = async (overrides: Record<string, any>) => {
    if (!pendingMatchTrade || !validationResult) return;
    setConfirmingMatch(true);
    setMatchError(null);
    setProcessingLabel(t('modifications.received.processingConfirm'));

    try {
      // Branch: Path 3 (confirm-create-modification) vs Path 2 (confirm-modification-match)
      // KAN-266: Pass emailTradeIndex for per-trade status tracking
      const response = isPath3Flow
        ? await clientService.confirmCreateModification(
            clientId, email.messageId, pendingMatchTrade.id,
            pendingMatchTrade.entityId || pendingMatchTrade.OurPartyId,
            pendingMatchTrade._path3ModificationType,
            validationResult.btn, overrides, email.emailTradeIndex
          )
        : await clientService.confirmModificationMatch(
            clientId, email.messageId, pendingMatchTrade.id, pendingMatchTrade.entityId,
            validationResult.btn, overrides, email.emailTradeIndex
          );

      setConfirmingMatch(false);
      if (response.success) {
        setMatchResult({
          action: 'confirmed_override',
          tradeNumber: pendingMatchTrade.TradeNumber,
          btn: validationResult.btn,
          comparisons: validationResult.comparisons,
          hasDiscrepancies: true,
          overrideFields: overrides,
          path3: isPath3Flow,
          modifiedFields: validationResult.modifiedFields || (response.data && response.data.modifiedFields),
        });
      } else {
        setMatchError(response.message || t('common.error'));
        setMatchingTradeId(null);
      }
    } catch {
      setMatchError(t('common.error'));
      setConfirmingMatch(false);
      setMatchingTradeId(null);
    }
  };

  /** Confirm price cascade — add SpotPrice + ForwardPoints to overrides and proceed. */
  const handlePriceCascadeConfirm = () => {
    if (!pendingOverrides || priceCascadeTarget === null) return;
    const spot = parseAmountInput(priceCascadeSpot);
    const fwd = parseAmountInput(priceCascadeFwd);
    if (spot === null || fwd === null) return;

    const enrichedOverrides = {
      ...pendingOverrides,
      SpotPrice: spot,
      ForwardPoints: fwd,
    };
    setShowPriceCascade(false);
    setPendingOverrides(null);
    setPriceCascadeTarget(null);
    executeOverrideAccept(enrichedOverrides);
  };

  const handlePriceCascadeCancel = () => {
    setShowPriceCascade(false);
    setPendingOverrides(null);
    setPriceCascadeTarget(null);
    setPriceCascadeSpot('');
    setPriceCascadeFwd('');
    // For Path 3, go back to trade picker
    if (isPath3Flow) {
      setValidationResult(null);
      setMatchingTradeId(null);
      setShowTradePickerInline(true);
    }
  };

  // Reject — trade stays PendingModification
  const handleRejectMatch = async () => {
    if (!pendingMatchTrade) return;
    setConfirmingMatch(true);
    setMatchError(null);
    setProcessingLabel(t('modifications.received.processingReject'));

    try {
      const response = await clientService.rejectModificationMatch(
        clientId, email.messageId, pendingMatchTrade.id, pendingMatchTrade.entityId,
        undefined, email.emailTradeIndex
      );
      setConfirmingMatch(false);
      if (response.success) {
        setMatchResult({
          action: 'rejected',
          tradeNumber: pendingMatchTrade.TradeNumber,
          comparisons: validationResult?.comparisons,
        });
        setValidationResult(null);
      } else {
        setMatchError(response.message || t('common.error'));
        setMatchingTradeId(null);
      }
    } catch {
      setMatchError(t('common.error'));
      setConfirmingMatch(false);
      setMatchingTradeId(null);
    }
  };

  // Close result and refresh data
  const handleCloseResult = () => {
    setMatchResult(null);
    setValidationResult(null);
    setPendingMatchTrade(null);
    setMatchingTradeId(null);
    onMatchSuccess();
  };

  return (
    <div className="received-detail-panel">
      {/* Header */}
      <div className="received-detail-header">
        <span className="received-email-intent-badge" style={{ backgroundColor: intentColour }}>
          {intentLabel}
        </span>
        <span className="received-detail-bank">{email.bankName || email.sender}</span>
        <span className="received-detail-date">{formatDivertedDate(email, i18n.language)}</span>
        <div style={{ flex: 1 }} />
        <button
          className="modifications-dismiss-btn"
          onClick={handleDismissClick}
          disabled={dismissing}
          title={t('confirmations.exceptions.dismiss')}
        >
          {dismissing
            ? <Loader2 size={14} className="modifications-spinner" />
            : <><Trash2 size={14} /><span>{t('confirmations.exceptions.dismiss')}</span></>
          }
        </button>
      </div>

      {/* Subject */}
      <div className="received-detail-subject">{email.subject}</div>

      {/* Content — split: PDF/text (2/3) + matching side panel (1/3) */}
      <div className="received-detail-content">
        {/* Left: PDF viewer or email body */}
        <div className="received-detail-preview-pane" ref={pdfContainerRef}>
          {email.pdfStoragePath ? (
            // PDF attachment available
            pdfLoading ? (
              <div className="received-pdf-loading">
                <Loader2 size={24} className="modifications-spinner" />
              </div>
            ) : pdfError || !pdfUrl ? (
              <div className="received-pdf-error">
                <AlertTriangle size={16} />
                <span>{t('modifications.received.pdfError')}</span>
              </div>
            ) : (
              <>
                {pdfNumPages > 1 && (
                  <div className="received-pdf-nav">
                    <button
                      onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))}
                      disabled={pdfPageNumber <= 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span>{pdfPageNumber} / {pdfNumPages}</span>
                    <button
                      onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages, p + 1))}
                      disabled={pdfPageNumber >= pdfNumPages}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                  loading={
                    <div className="received-pdf-loading">
                      <Loader2 size={24} className="modifications-spinner" />
                    </div>
                  }
                >
                  <Page
                    pageNumber={pdfPageNumber}
                    width={pdfWidth > 0 ? pdfWidth : undefined}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </>
            )
          ) : (
            // No PDF — show email body text
            <>
              <div className="received-detail-preview-label">{t('modifications.received.emailPreview')}</div>
              {email.emailBodyPreview ? (
                <pre className="received-detail-preview">{email.emailBodyPreview}</pre>
              ) : (
                <span className="received-email-no-preview">{t('modifications.received.noPreview')}</span>
              )}
            </>
          )}

          {dismissError && (
            <div className="received-email-error">
              {t('confirmations.exceptions.dismissDivertedError')}
            </div>
          )}
        </div>

        {/* Right: matching side panel */}
        <div className="received-detail-match-pane">
          <div className="match-pane-header">
            {showTradePickerInline ? (
              <>
                <button className="match-pane-back-btn" onClick={() => setShowTradePickerInline(false)}>
                  <ChevronLeft size={16} />
                </button>
                <span className="match-pane-title">{t('modifications.received.tradePickerTitle')}</span>
              </>
            ) : (
              <span className="match-pane-title">{t('modifications.received.matchTitle')}</span>
            )}
          </div>

          {showTradePickerInline ? (
            /* Path 3 — inline trade picker */
            <div className="match-pane-trade-picker">
              {tradePickerLoading ? (
                <div className="match-pane-loading">
                  <Loader2 size={20} className="modifications-spinner" />
                  <span>{t('common.loading')}</span>
                </div>
              ) : tradePickerTrades.length === 0 ? (
                <div className="match-pane-empty">
                  <Inbox size={24} strokeWidth={1.5} />
                  <span>{t('modifications.empty.eligible')}</span>
                </div>
              ) : (
                <div className="trade-picker-grid ag-theme-alpine-dark">
                  <AgGridReact
                    ref={tradePickerGridRef}
                    rowData={tradePickerTrades}
                    columnDefs={[
                      { headerName: t('modifications.columns.tradeNumber'), field: 'TradeNumber', width: 110, sortable: true, filter: true },
                      { headerName: t('modifications.columns.bankTradeNumber'), field: 'bankTradeNumber', width: 120, filter: true },
                      { headerName: t('modifications.columns.tradeDate'), field: 'TradeDate', width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
                      { headerName: t('modifications.columns.maturityDate'), field: 'MaturityDate', width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
                      { headerName: t('modifications.columns.direction'), field: 'Direction', width: 80 },
                      { headerName: t('modifications.columns.amount'), field: 'QuantityCurrency1', width: 120, sortable: true, valueFormatter: (p: any) => formatNumber(p.value) },
                      { headerName: t('modifications.columns.ccy1'), field: 'Currency1', width: 70 },
                      { headerName: t('modifications.columns.ccy2'), field: 'Currency2', width: 70 },
                      { headerName: t('modifications.columns.price'), field: 'Price', width: 100, valueFormatter: (p: any) => formatNumber(p.value) },
                      { headerName: t('modifications.columns.counterparty'), field: 'CounterpartyName', width: 140, filter: true },
                      { headerName: t('modifications.columns.entity'), field: 'OurParty', width: 140, filter: true },
                      { headerName: t('modifications.columns.settlementType'), field: 'SettlementType', width: 130, filter: true },
                      { headerName: t('modifications.columns.state'), field: 'workflowState', width: 120, filter: true },
                    ]}
                    defaultColDef={{ sortable: true, resizable: true, minWidth: 60 }}
                    rowSelection="single"
                    onSelectionChanged={(e) => {
                      const rows = e.api.getSelectedRows();
                      setSelectedPickerTrade(rows.length > 0 ? rows[0] : null);
                    }}
                    onGridReady={(e) => {
                      // Auto-select the BTN-matched row if one was pre-selected
                      if (selectedPickerTrade) {
                        e.api.forEachNode(node => {
                          if (node.data?.id === selectedPickerTrade.id) {
                            node.setSelected(true);
                          }
                        });
                      }
                    }}
                    getRowClass={(params) => params.data?.btnMatchedInEmail ? 'trade-picker-btn-match' : ''}
                    getRowId={(params) => params.data.id}
                    localeText={agGridLocale}
                    domLayout="normal"
                  />
                </div>
              )}
              <div className="trade-picker-footer">
                <div className="trade-picker-type-select">
                  <label>{t('modifications.received.modTypeLabel')}:</label>
                  <select
                    value={pickerModType}
                    onChange={e => setPickerModType(e.target.value)}
                  >
                    <option value="">{t('modifications.received.selectModType')}</option>
                    <option value="Rollover">{t('modifications.types.rollover')}</option>
                    <option value="SettlementTypeChange">{t('modifications.types.settlementTypeChange')}</option>
                  </select>
                </div>
                <button
                  className="match-validation-accept-btn"
                  onClick={handleTradePickerContinue}
                  disabled={!selectedPickerTrade || !pickerModType}
                >
                  {t('modifications.received.continueBtn')}
                </button>
              </div>
            </div>
          ) : candidatesLoading ? (
            <div className="match-pane-loading">
              <Loader2 size={20} className="modifications-spinner" />
              <span>{t('common.loading')}</span>
            </div>
          ) : candidateTrades.length > 0 ? (
            /* Path 2 — candidate trades found */
            <div className="match-pane-candidates">
              <div className="match-pane-hint">{t('modifications.received.matchHint')}</div>
              {candidateTrades.map(trade => {
                const modTypeKey = trade.modificationType
                  ? `modifications.types.${trade.modificationType.charAt(0).toLowerCase()}${trade.modificationType.slice(1)}`
                  : '';
                const modTypeLabel = trade.modificationType
                  ? t(modTypeKey, trade.modificationType)
                  : '—';
                const isExpanded = expandedCandidates.has(trade.id);
                const toggleExpand = () => {
                  setExpandedCandidates(prev => {
                    const next = new Set(prev);
                    if (next.has(trade.id)) next.delete(trade.id);
                    else next.add(trade.id);
                    return next;
                  });
                };
                const HIDDEN_UNWIND_FIELDS = ['UnwindCalculationMode', 'UnwindSettlementOverridden', 'UnwindSettlementCurrency', 'UnwindGrossResult', 'UnwindDiscountConvention'];
                const changedFields = Object.keys(trade.modifiedFields || {}).filter(f => !HIDDEN_UNWIND_FIELDS.includes(f)).sort((a, b) => {
                  const ia = FIELD_ORDER.indexOf(a);
                  const ib = FIELD_ORDER.indexOf(b);
                  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                });
                const fmtDate = (v: string) => {
                  const d = new Date(v);
                  const dd = String(d.getUTCDate()).padStart(2, '0');
                  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
                  const yyyy = d.getUTCFullYear();
                  return `${dd}/${mm}/${yyyy}`;
                };
                const formatFieldValue = (field: string, value: any) => {
                  if (value == null) return '—';
                  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                    return fmtDate(value);
                  }
                  if (typeof value === 'number') {
                    return field === 'Price' || field === 'SpotPrice' || field === 'ForwardPoints'
                      ? value.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                      : value.toLocaleString(i18n.language, { maximumFractionDigits: 0 });
                  }
                  return String(value);
                };
                const fieldLabel = (f: string) => {
                  const camel = f.charAt(0).toLowerCase() + f.slice(1);
                  return t(`modifications.fields.${camel}`, f);
                };
                return (
                  <div key={trade.id} className={`match-candidate-card${isExpanded ? ' expanded' : ''}`}>
                    <div className="match-candidate-header" onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="match-candidate-trade">#{trade.TradeNumber}</span>
                      <span className="match-candidate-summary-inline">
                        {trade.CounterpartyName} · {trade.Direction} {trade.Currency1}/{trade.Currency2}
                      </span>
                      <span className="match-candidate-type">{modTypeLabel}</span>
                      {trade.modificationStatus === 'pending' && (
                        <span className="match-candidate-pending-badge">
                          <Clock size={11} />
                          {t('modifications.received.awaitingApproval')}
                        </span>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="match-candidate-body">
                        {changedFields.length > 0 ? (
                          <table className="match-candidate-changes">
                            <thead>
                              <tr>
                                <th>{t('modifications.detail.fieldLabel')}</th>
                                <th>{t('modifications.resultModal.from')}</th>
                                <th>{t('modifications.resultModal.to')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {changedFields.map(f => (
                                <tr key={f}>
                                  <td>{fieldLabel(f)}</td>
                                  <td className="match-change-from">{formatFieldValue(f, (trade.originalFieldValues || {})[f])}</td>
                                  <td className="match-change-to">{formatFieldValue(f, trade.modifiedFields[f])}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="match-candidate-no-changes">
                            {trade.modificationStatus === 'pending'
                              ? t('modifications.received.notYetApproved')
                              : trade.comment || t('modifications.received.noFieldChanges')}
                          </div>
                        )}
                        {trade.confirmedByName && (
                          <div className="match-candidate-meta">
                            {t('modifications.columns.approvedBy')}: {trade.confirmedByName}
                            {trade.confirmedAt && (
                              <> · {fmtDate(trade.confirmedAt)}</>
                            )}
                          </div>
                        )}
                        <button
                          className="match-candidate-btn"
                          onClick={() => handleMatch(trade)}
                          disabled={matchingTradeId !== null || validating || confirmingMatch || trade.modificationStatus === 'pending'}
                        >
                          {(validating || confirmingMatch) && matchingTradeId === trade.id
                            ? <><Loader2 size={14} className="modifications-spinner" /> {validating ? t('modifications.received.validating') : t('modifications.received.confirming')}</>
                            : <><CheckCircle2 size={14} /> {t('modifications.received.matchBtn')}</>
                          }
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {matchError && (
                <div className="match-pane-error">
                  <AlertTriangle size={14} />
                  <span>{matchError}</span>
                </div>
              )}

              {/* Path 3 button — always visible below candidates */}
              <button
                className="match-create-modification-btn"
                onClick={handleOpenTradePicker}
                disabled={matchingTradeId !== null || validating || confirmingMatch}
              >
                <FileText size={14} />
                {t('modifications.received.createModification')}
              </button>
            </div>
          ) : (
            /* Path 3 — no candidates */
            <div className="match-pane-empty">
              <Inbox size={24} strokeWidth={1.5} />
              <span>{t('modifications.received.noMatchingTrades')}</span>
              <button
                className="match-create-modification-btn"
                onClick={handleOpenTradePicker}
                disabled={matchingTradeId !== null || validating || confirmingMatch}
              >
                <FileText size={14} />
                {t('modifications.received.createModification')}
              </button>
            </div>
          )}

          {/* Validation result — discrepancy table (rendered outside candidates/empty branches) */}
          {validationResult && validationResult.hasDiscrepancies && (
            <div className="match-validation-result">
              <div className="match-validation-header">
                <AlertTriangle size={16} />
                <span>{t('modifications.received.discrepanciesFound')}</span>
              </div>
              {validationResult.modificationTypeMismatch && (() => {
                const humanize = (s: string) => s.replace(/([a-z])([A-Z])/g, '$1 $2');
                const cpType = validationResult.inferredCounterpartyType;
                const tradeType = validationResult.modificationType;
                const cpKey = cpType ? `modifications.types.${cpType.charAt(0).toLowerCase()}${cpType.slice(1)}` : '';
                const tradeKey = tradeType ? `modifications.types.${tradeType.charAt(0).toLowerCase()}${tradeType.slice(1)}` : '';
                const cpLabel = cpType ? t(cpKey, humanize(cpType)) : '';
                const tradeLabel = tradeType ? t(tradeKey, humanize(tradeType)) : '';
                return (
                  <div className="match-type-mismatch-warning">
                    <AlertTriangle size={14} />
                    <span>{t('modifications.received.modificationTypeMismatch', {
                      counterpartyType: cpLabel,
                      tradeType: tradeLabel,
                    })}</span>
                  </div>
                );
              })()}
              <table className="match-validation-table">
                <thead>
                  <tr>
                    <th>{t('modifications.detail.fieldLabel')}</th>
                    <th>{t('modifications.received.tradeValue')}</th>
                    <th>{t('modifications.received.bankValue')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {prepareComparisons(validationResult.comparisons || [])
                    .map((c: any) => {
                      const camel = c.field.charAt(0).toLowerCase() + c.field.slice(1);
                      return (
                        <tr key={c.field} className={c.result === 'mismatch' ? 'validation-mismatch' : ''}>
                          <td>{t(`modifications.fields.${camel}`, c.field)}</td>
                          <td>{formatComparisonValue(c.field, c.clientValue)}</td>
                          <td className={c.result === 'mismatch' ? 'match-change-to' : ''}>
                            {formatComparisonValue(c.field, c.emailValue)}
                          </td>
                          <td>
                            {c.result === 'match'
                              ? <CheckCircle2 size={14} className="validation-match-icon" />
                              : <XCircle size={14} className="validation-mismatch-icon" />
                            }
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
              {validationResult.btn && (
                <div className="match-validation-btn-info">
                  {t('modifications.received.bankTradeNumberLabel')}: <strong>{validationResult.btn}</strong>
                </div>
              )}
              {validationResult.modificationTypeMismatch ? (
                <div className="match-validation-actions">
                  <button
                    className="match-validation-reject-btn"
                    onClick={() => { setValidationResult(null); setMatchingTradeId(null); }}
                  >
                    <XCircle size={14} />
                    <span>{t('common.close')}</span>
                  </button>
                </div>
              ) : (
                <div className="match-validation-actions">
                  <button
                    className="match-validation-reject-btn"
                    onClick={handleRejectMatch}
                    disabled={confirmingMatch}
                  >
                    <XCircle size={14} />
                    <span>{t('modifications.received.rejectBtn')}</span>
                  </button>
                  <button
                    className="match-validation-accept-btn"
                    onClick={handleOverrideAccept}
                    disabled={confirmingMatch}
                  >
                    {confirmingMatch
                      ? <Loader2 size={14} className="modifications-spinner" />
                      : <CheckCircle2 size={14} />
                    }
                    <span>{t('modifications.received.overrideAcceptBtn')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Identity mismatch modal (Path 3 — wrong trade selected) */}
      {validationResult && validationResult.identityMismatch && (
        <div className="identity-mismatch-overlay">
          <div className="identity-mismatch-modal">
            <div className="identity-mismatch-modal-header">
              <AlertTriangle size={20} />
              <span>{t('modifications.received.identityMismatchTitle')}</span>
            </div>
            <p className="identity-mismatch-desc">
              {t('modifications.received.identityMismatchDesc')}
            </p>
            <table className="match-validation-table">
              <thead>
                <tr>
                  <th>{t('modifications.detail.fieldLabel')}</th>
                  <th>{t('modifications.received.tradeValue')}</th>
                  <th>{t('modifications.received.bankValue')}</th>
                </tr>
              </thead>
              <tbody>
                {(validationResult.identityMismatches || []).map((m: any) => {
                  const camel = m.field.charAt(0).toLowerCase() + m.field.slice(1);
                  return (
                    <tr key={m.field} className="validation-mismatch">
                      <td>{t(`modifications.fields.${camel}`, m.field)}</td>
                      <td>{formatComparisonValue(m.field, m.clientValue)}</td>
                      <td className="match-change-to">
                        {formatComparisonValue(m.field, m.emailValue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="identity-mismatch-modal-actions">
              <button
                className="match-validation-reject-btn"
                onClick={() => {
                  setValidationResult(null);
                  setMatchingTradeId(null);
                  setShowTradePickerInline(true);
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing mode modal */}
      {showModeModal && (() => {
        const cptyMethod = pendingMatchTrade?.counterpartyModificationMethod;
        const confirmationPreferred = cptyMethod?.startsWith('confirmation');
        const contractPreferred = cptyMethod === 'contract';
        const hasPref = confirmationPreferred || contractPreferred;
        return (
          <div className="match-mode-overlay" onClick={() => { setShowModeModal(false); setModeWarningPending(null); }}>
            <div className="match-mode-modal" onClick={e => e.stopPropagation()}>
              <div className="match-mode-header">
                <span>{t('modifications.received.modeModalTitle')}</span>
                <button className="match-mode-close" onClick={() => { setShowModeModal(false); setModeWarningPending(null); }}>
                  <X size={16} />
                </button>
              </div>
              <div className="match-mode-body">
                <p>{cptyMethod
                  ? t(`modifications.received.modeModalDesc${
                      cptyMethod === 'confirmation-email' ? 'ConfirmationEmail'
                      : cptyMethod === 'confirmation-pdf' ? 'ConfirmationPdf'
                      : 'Contract'
                    }`, { counterparty: pendingMatchTrade?.CounterpartyName || '' })
                  : t('modifications.received.modeModalDesc')
                }</p>
                <div className="match-mode-buttons">
                  <button
                    className={`match-mode-btn confirmation${hasPref && !confirmationPreferred ? ' match-mode-btn-muted' : ''}`}
                    onClick={() => handleModeClick('confirmation')}
                  >
                    <Mail size={20} />
                    <span>{confirmationPreferred
                      ? t('modifications.received.modeConfirmationYes')
                      : contractPreferred
                        ? t('modifications.received.modeContractNo')
                        : t('modifications.received.modeConfirmation')
                    }</span>
                    {!hasPref && <small>{t('modifications.received.modeConfirmationDesc')}</small>}
                  </button>
                  <button
                    className={`match-mode-btn contract${hasPref && !contractPreferred ? ' match-mode-btn-muted' : ''}`}
                    onClick={() => handleModeClick('contract')}
                    disabled={!email.pdfStoragePath}
                    title={!email.pdfStoragePath ? t('modifications.received.noPdfAvailable') : ''}
                  >
                    <FileText size={20} />
                    <span>{contractPreferred
                      ? t('modifications.received.modeContractYes')
                      : confirmationPreferred
                        ? t('modifications.received.modeConfirmationNo')
                        : t('modifications.received.modeContract')
                    }</span>
                    {!hasPref && <small>{t('modifications.received.modeContractDesc')}</small>}
                  </button>
                </div>
                {modeWarningPending && (
                  <div className="match-mode-warning">
                    <AlertTriangle size={14} />
                    <span>{t('modifications.received.modeMethodWarning', {
                      counterparty: pendingMatchTrade?.CounterpartyName || '',
                      expectedMethod: cptyMethod === 'contract'
                        ? t('modifications.received.modeContract')
                        : t('modifications.received.modeConfirmation'),
                    })}</span>
                    <div className="match-mode-warning-actions">
                      <button onClick={() => setModeWarningPending(null)}>
                        {t('common.cancel')}
                      </button>
                      <button onClick={() => { setModeWarningPending(null); handleModeSelect(modeWarningPending); }}>
                        {t('modifications.received.modeContinueAnyway')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Full-page blocking overlay during LLM processing */}
      {(validating || confirmingMatch) && (
        <div className="match-processing-overlay">
          <div className="match-processing-content">
            <Loader2 size={36} className="modifications-spinner" />
            <span className="match-processing-label">{processingLabel}</span>
            <span className="match-processing-sub">
              {pendingMatchTrade?.TradeNumber && `${t('modifications.columns.tradeNumber')} ${pendingMatchTrade.TradeNumber}`}
            </span>
          </div>
        </div>
      )}

      {/* Match result modal */}
      {matchResult && (
        <div className="match-processing-overlay">
          <div className="match-result-modal">
            <div className="match-result-icon">
              {matchResult.action === 'rejected'
                ? <XCircle size={40} style={{ color: '#ef4444' }} />
                : <CheckCircle2 size={40} style={{ color: '#22c55e' }} />
              }
            </div>
            <h3 className="match-result-title">
              {matchResult.action === 'rejected'
                ? t('modifications.received.resultRejected')
                : matchResult.action === 'confirmed_override'
                  ? t('modifications.received.resultConfirmedOverride')
                  : t('modifications.received.resultConfirmed')
              }
            </h3>
            <div className="match-result-trade">
              {t('modifications.columns.tradeNumber')} {matchResult.tradeNumber}
            </div>

            {matchResult.action !== 'rejected' && (
              <>
                {matchResult.btn && (
                  <div className="match-result-btn-line">
                    {t('modifications.received.bankTradeNumberLabel')}: <strong>{matchResult.btn}</strong>
                  </div>
                )}
                <div className="match-result-status">
                  {matchResult.path3
                    ? t('modifications.received.resultModificationCreated')
                    : t('modifications.received.resultTradeRestored')
                  }
                </div>

                {/* Path 3: show only modified fields with before→after */}
                {matchResult.path3 && matchResult.comparisons && matchResult.comparisons.length > 0 && (() => {
                  const modSet = new Set(matchResult.modifiedFields || []);
                  const changed = prepareComparisons(matchResult.comparisons).filter((c: any) => modSet.has(c.field));
                  if (changed.length === 0) return null;
                  return (
                    <div className="path3-changes-summary">
                      <div className="path3-changes-label">{t('modifications.received.changesApplied')}</div>
                      <table className="match-result-comparison-table">
                        <thead>
                          <tr>
                            <th>{t('modifications.detail.fieldLabel')}</th>
                            <th>{t('modifications.received.previousValue')}</th>
                            <th>{t('modifications.received.newValue')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changed.map((c: any) => {
                            const camel = c.field.charAt(0).toLowerCase() + c.field.slice(1);
                            return (
                              <tr key={c.field}>
                                <td>{t(`modifications.fields.${camel}`, c.field)}</td>
                                <td className="path3-old-value">{formatComparisonValue(c.field, c.clientValue)}</td>
                                <td className="path3-new-value">{formatComparisonValue(c.field, c.emailValue)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* Path 2: full comparison table with match/mismatch icons */}
                {!matchResult.path3 && matchResult.comparisons && matchResult.comparisons.length > 0 && (
                  <table className="match-result-comparison-table">
                    <thead>
                      <tr>
                        <th>{t('modifications.detail.fieldLabel')}</th>
                        <th>{t('modifications.received.tradeValue')}</th>
                        <th>{t('modifications.received.bankValue')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {prepareComparisons(matchResult.comparisons)
                        .map((c: any) => {
                          const camel = c.field.charAt(0).toLowerCase() + c.field.slice(1);
                          const wasOverridden = matchResult.overrideFields && c.field in matchResult.overrideFields;
                          return (
                            <tr key={c.field} className={c.result === 'mismatch' ? 'validation-mismatch' : ''}>
                              <td>{t(`modifications.fields.${camel}`, c.field)}</td>
                              <td>{formatComparisonValue(c.field, c.clientValue)}</td>
                              <td className={c.result === 'mismatch' ? 'match-change-to' : ''}>
                                {formatComparisonValue(c.field, c.emailValue)}
                              </td>
                              <td>
                                {c.result === 'match'
                                  ? <CheckCircle2 size={14} className="validation-match-icon" />
                                  : wasOverridden
                                    ? <span className="match-result-overridden">{t('modifications.received.resultOverridden')}</span>
                                    : <XCircle size={14} className="validation-mismatch-icon" />
                                }
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                )}
              </>
            )}

            {matchResult.action === 'rejected' && (
              <>
                <div className="match-result-status">
                  {t('modifications.received.resultTradeStays')}
                </div>
                <div className="match-result-status" style={{ marginTop: '4px' }}>
                  {t('modifications.received.resultEmailDismissed')}
                </div>
              </>
            )}

            <button className="match-result-ok-btn" onClick={handleCloseResult}>
              {t('common.ok') || 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Price cascade modal — shown when override includes Price on a forward trade */}
      {showPriceCascade && priceCascadeTarget !== null && (() => {
        const spot = parseAmountInput(priceCascadeSpot);
        const fwd = parseAmountInput(priceCascadeFwd);
        const sum = (spot ?? 0) + (fwd ?? 0);
        const isValid = spot !== null && fwd !== null && Math.abs(sum - priceCascadeTarget) < 0.001;
        return (
          <div className="match-mode-overlay" onClick={handlePriceCascadeCancel}>
            <div className="match-mode-modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
              <div className="match-mode-header">
                <span>{t('confirmations.priceCascadeTitle')}</span>
                <button className="match-mode-close" onClick={handlePriceCascadeCancel}>
                  <X size={16} />
                </button>
              </div>
              <div className="match-mode-body" style={{ padding: '16px 20px' }}>
                <div style={{ marginBottom: 12, fontSize: '0.85rem', color: 'var(--text-secondary, #94a3b8)' }}>
                  {t('confirmations.priceCascadeSubtitle')}
                </div>
                <div style={{ marginBottom: 16, fontSize: '0.9rem' }}>
                  {t('modifications.fields.price')}: {pendingMatchTrade?.Price ?? '—'} → <strong>{priceCascadeTarget}</strong>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '0.82rem' }}>{t('grid.columns.spotPrice')}</span>
                    <input
                      type="text"
                      value={priceCascadeSpot}
                      onChange={e => setPriceCascadeSpot(e.target.value)}
                      style={{ width: 160, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color, #334155)', background: 'var(--bg-primary, #0f172a)', color: 'var(--text-primary, #e2e8f0)', fontSize: '0.85rem', textAlign: 'right' }}
                    />
                  </label>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '0.82rem' }}>{t('grid.columns.forwardPoints')}</span>
                    <input
                      type="text"
                      value={priceCascadeFwd}
                      onChange={e => setPriceCascadeFwd(e.target.value)}
                      style={{ width: 160, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color, #334155)', background: 'var(--bg-primary, #0f172a)', color: 'var(--text-primary, #e2e8f0)', fontSize: '0.85rem', textAlign: 'right' }}
                    />
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '0.82rem' }}>{t('confirmations.priceCascadeSum')}</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: isValid ? '#22c55e' : '#ef4444' }}>
                      {(spot === null || fwd === null) ? '—' : sum.toFixed(4)}
                      {!isValid && spot !== null && fwd !== null && (
                        <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: 6 }}>
                          {t('confirmations.priceCascadeSumMismatch', { price: priceCascadeTarget })}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                  <button
                    className="match-validation-reject-btn"
                    onClick={handlePriceCascadeCancel}
                    style={{ padding: '6px 16px' }}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="match-validation-accept-btn"
                    onClick={handlePriceCascadeConfirm}
                    disabled={!isValid}
                    style={{ padding: '6px 16px', opacity: isValid ? 1 : 0.5 }}
                  >
                    {t('confirmations.accept')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog
        isOpen={showDismissConfirm}
        title={t('modifications.received.dismissConfirmTitle')}
        message={t('modifications.received.dismissConfirmMessage')}
        confirmLabel={t('confirmations.exceptions.dismiss')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDismissConfirm}
        onCancel={() => setShowDismissConfirm(false)}
        type="warning"
      />
    </div>
  );
};

// ── Tab root ───────────────────────────────────────────────────────────────

const ReceivedModificationsTab: React.FC<{
  items: DivertedEmail[];
  loading: boolean;
  clientId: string;
  onDismiss: (emailItem: DivertedEmail) => Promise<boolean>;
  onMatchSuccess: () => void;
}> = ({ items, loading, clientId, onDismiss, onMatchSuccess }) => {
  const { t } = useTranslation();
  const [selectedEmail, setSelectedEmail] = useState<DivertedEmail | null>(null);

  // KAN-266: Composite key for multi-trade rows — {messageId}-{emailTradeIndex}
  const rowKey = (e: DivertedEmail) => `${e.messageId}-${e.emailTradeIndex ?? 0}`;

  // Keep selected email in sync if items list changes (e.g. after dismiss)
  React.useEffect(() => {
    if (selectedEmail && !items.find(i => rowKey(i) === rowKey(selectedEmail))) {
      setSelectedEmail(null);
    }
  }, [items, selectedEmail]);

  const handleDismiss = async (emailItem: DivertedEmail): Promise<boolean> => {
    const ok = await onDismiss(emailItem);
    return ok;
  };

  if (loading) {
    return (
      <div className="modifications-loading">
        <Loader2 size={32} className="modifications-spinner" />
        {t('common.loading')}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="modifications-empty">
        <div className="modifications-empty-content">
          <Mail size={36} className="modifications-empty-icon" />
          <span>{t('modifications.empty.received')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="received-modifications-layout">
      {/* Left — condensed list */}
      <div className="received-email-list-pane">
        {items.map(item => (
          <ReceivedEmailListItem
            key={rowKey(item)}
            item={item}
            isSelected={!!selectedEmail && rowKey(item) === rowKey(selectedEmail)}
            onSelect={setSelectedEmail}
            onDismiss={handleDismiss}
          />
        ))}
      </div>

      {/* Right — detail panel */}
      <div className="received-email-detail-pane">
        {selectedEmail ? (
          <ReceivedEmailPanel email={selectedEmail} clientId={clientId} onDismiss={handleDismiss} onMatchSuccess={onMatchSuccess} />
        ) : (
          <div className="received-detail-empty">
            <Mail size={40} strokeWidth={1} />
            <span>{t('modifications.received.selectPrompt')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modifications;
